/**
 * Flight Detail Page
 * View and edit flight details, stats, email template info
 */

import React, { useState, useEffect } from "react";
import {
  effectiveSendCategories,
  resolvePoolDisplay,
} from "../utils/sendgridDelivery";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import FlightForm from "../components/campaigns/FlightForm";
import TemplatePreviewModal from "../components/campaigns/TemplatePreviewModal";
import PreSendConfirmationModal from "../components/campaigns/PreSendConfirmationModal";
import SendTestEmailModal from "../components/campaigns/SendTestEmailModal";
import AudienceExclusionsPanel from "../components/campaigns/AudienceExclusionsPanel";
import FlightOpenerExportPanel from "../components/campaigns/FlightOpenerExportPanel";
import FlightNonOpenerExportPanel from "../components/campaigns/FlightNonOpenerExportPanel";
import FlightUnsubscribeExportPanel from "../components/campaigns/FlightUnsubscribeExportPanel";
import EmailCheckStatusLine from "../components/campaigns/EmailCheckStatusLine";
import EibExcludeRiskyToggle from "../components/campaigns/EibExcludeRiskyToggle";
import DeletedCampaignNotice from "../components/campaigns/DeletedCampaignNotice";
import { isCampaignVisible } from "../utils/campaignSoftDelete";
import { supabase } from "../services/supabase";
import { formatScheduledDateTime } from "../utils/formatters";
import { isSafeInternalPath } from "../utils/navigation";
import {
  getCampaign,
  getFlight,
  getCampaignFlights,
  getReusableVoucherCountsByFlight,
  getFlightRecipientTableProgress,
  getFlightRecoverySplits,
  getFlightDeferralSummary,
  getFlightBounceSummary,
  generateFlightVouchers,
  updateFlight,
  deleteFlight,
  duplicateFlight,
  FLIGHT_STATUS_OPTIONS,
  type FlightRecipientTableProgress,
  type FlightRecoverySplit,
  type FlightDeferralSummary,
  type FlightBounceSummary,
} from "../services/campaigns.service";
import {
  getSendGridTemplateDetails,
  type SendGridTemplateDetails,
  type FieldMapping,
} from "../services/templates.service";
import { downloadEibRecipientsCsv } from "../services/eventInABox.service";
import type {
  Campaign,
  CampaignFlight,
  UpdateFlightDTO,
} from "../types/campaign.types";

/** G3 informational note reasons (migration 411): 'role', 'synthetic' or 'role,synthetic'. Never hold a send. */
function isG3NoteOnlyReason(reason: unknown): boolean {
  if (typeof reason !== "string" || reason.length === 0) {
    return false;
  }
  const parts = reason
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);
  return (
    parts.length > 0 && parts.every((r) => r === "role" || r === "synthetic")
  );
}

const FlightDetail: React.FC = () => {
  const { campaignId, flightId } = useParams<{
    campaignId: string;
    flightId: string;
  }>();
  const navigate = useNavigate();
  // R7 (2026-05-25): validate backTo through isSafeInternalPath to block
  // open-redirect via crafted ?backTo=javascript:... or //evil.com.
  const [searchParams] = useSearchParams();
  const rawBackTo = searchParams.get("backTo");
  const backTo = isSafeInternalPath(rawBackTo) ? rawBackTo : null;

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [flight, setFlight] = useState<CampaignFlight | null>(null);
  // Sibling flights in this campaign — needed so the Edit form's reminder
  // "source voucher flight" picker has candidates (the bug: it was empty here
  // because FlightDetail never passed siblingFlights).
  const [siblingFlights, setSiblingFlights] = useState<CampaignFlight[]>([]);
  const [recipientProgress, setRecipientProgress] =
    useState<FlightRecipientTableProgress | null>(null);
  // 2026-06-08: surface the recovery sub-flights when a `failed` parent
  // was rescued via the manual-split pattern (see
  // metadata.stuck_partial_send.recovery_split_count). Without this the
  // parent flight page reads as "nothing was sent" — misleading because
  // the splits did the work.
  const [recoverySplits, setRecoverySplits] = useState<FlightRecoverySplit[]>(
    [],
  );
  const [deferralSummary, setDeferralSummary] =
    useState<FlightDeferralSummary | null>(null);
  const [bounceSummary, setBounceSummary] =
    useState<FlightBounceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [regeneratingVouchers, setRegeneratingVouchers] = useState(false);
  const [exportingRecipients, setExportingRecipients] = useState(false);

  // G3 pre-send validation Health dialog: block (400) or warn (409) from the send gate.
  const [g3Gate, setG3Gate] = useState<{
    kind: "block" | "warn";
    context: "send" | "schedule";
    reason?: string; // invalid | synthetic | role
    total?: number;
    invalid?: number;
    synthetic?: number;
    role?: number;
    invalid_ratio?: number;
    synthetic_ratio?: number;
    role_ratio?: number;
    fingerprint?: string;
  } | null>(null);
  const [g3OverrideReason, setG3OverrideReason] = useState("");
  const [g3Busy, setG3Busy] = useState(false);

  // Email template preview
  const [showPreview, setShowPreview] = useState(false);
  const [templateDetails, setTemplateDetails] =
    useState<SendGridTemplateDetails | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Phase 1B: pre-send confirmation + send test. The PreSendConfirmationModal
  // owns its own template fetch so a fetch failure is a hard block (rather
  // than silently letting the unmapped-variable check pass on null
  // merge_fields).
  const [showPreSendModal, setShowPreSendModal] = useState(false);
  const [showSendNowModal, setShowSendNowModal] = useState(false);
  const [showTestSendModal, setShowTestSendModal] = useState(false);
  // Schedule edit / cancel state (added 2026-05-13)
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
  const [editScheduleAt, setEditScheduleAt] = useState("");
  const [showCancelScheduleConfirm, setShowCancelScheduleConfirm] =
    useState(false);
  // 2026-05-27: when the operator clicks Schedule on a draft flight that
  // has no scheduled_send_at, we route them through Edit Schedule first
  // to pick a date. After saving a date, the PreSend flow opens
  // automatically — this flag carries that continuation across the
  // modal chain.
  const [openPreSendAfterScheduleSave, setOpenPreSendAfterScheduleSave] =
    useState(false);
  const [sendingNow, setSendingNow] = useState(false);

  useEffect(() => {
    if (campaignId && flightId) {
      loadData();
    }
  }, [campaignId, flightId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignData, flightData, siblingData] = await Promise.all([
        getCampaign(campaignId!),
        getFlight(flightId!),
        getCampaignFlights(campaignId!),
      ]);
      // Annotate siblings with reusable voucher counts so a legacy source flight
      // (vouchers minted but generate_vouchers stale) still qualifies. Best-effort.
      let siblings = siblingData;
      try {
        const counts = await getReusableVoucherCountsByFlight(
          siblings.map((f) => f.id),
        );
        siblings = siblings.map((f) => ({
          ...f,
          reusable_voucher_count: counts[f.id] ?? 0,
        }));
      } catch {
        /* non-fatal — eligibility falls back to the generate_vouchers flag */
      }
      setSiblingFlights(siblings);
      const usesRecipientTable =
        Boolean(
          (flightData?.metadata as Record<string, unknown> | undefined)
            ?.send_mode === "recipient_table",
        ) ||
        Boolean(flightData?.send_recipients_snapshot_complete_at) ||
        Boolean(flightData?.send_snapshot_progress);
      const progressData =
        flightData && usesRecipientTable
          ? await getFlightRecipientTableProgress(flightData.id)
          : null;
      // 2026-06-08: when the parent flight failed mid-send AND has
      // recovery metadata, fetch the sub-flights so the UI can show
      // the operator the recovery state instead of just "failed."
      const stuckPartial = (
        flightData?.metadata as Record<string, unknown> | undefined
      )?.["stuck_partial_send"] as Record<string, unknown> | undefined;
      const recoverySplitCount =
        typeof stuckPartial?.["recovery_split_count"] === "number"
          ? (stuckPartial["recovery_split_count"] as number)
          : 0;
      const splitsData =
        flightData && flightData.status === "failed" && recoverySplitCount > 0
          ? await getFlightRecoverySplits(
              flightData.id,
              flightData.campaign_id,
              flightData.name,
            )
          : [];
      // Deferral-visibility: when the flight has recipients currently deferred/throttled by the
      // receiving provider, fetch the reason + provider-concentration breakdown for the panel.
      const deferralData =
        flightData && (flightData.emails_deferred ?? 0) > 0
          ? await getFlightDeferralSummary(flightData.id)
          : null;
      // G4: when the flight bounced anyone, fetch the hard/soft/spam + reason/domain breakdown.
      const bounceData =
        flightData && (flightData.emails_bounced ?? 0) > 0
          ? await getFlightBounceSummary(flightData.id)
          : null;
      setCampaign(campaignData);
      setFlight(flightData);
      setRecipientProgress(progressData);
      setRecoverySplits(splitsData);
      setDeferralSummary(deferralData);
      setBounceSummary(bounceData);
    } catch (err) {
      console.error("Error loading flight:", err);
      setError("Failed to load flight details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (data: any) => {
    try {
      setSubmitting(true);
      setError(null);
      const updateData: UpdateFlightDTO = { ...data };
      delete (updateData as any).campaign_id;
      await updateFlight(flightId!, updateData);
      setSuccess("Flight updated successfully");
      setIsEditing(false);
      loadData();
    } catch (err) {
      console.error("Error updating flight:", err);
      setError("Failed to update flight");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDuplicate = async () => {
    if (!flight) {
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const newFlight = await duplicateFlight(flight.id);
      setSuccess(`Duplicated as "${newFlight.name}"`);
      navigate(`/campaigns/${flight.campaign_id}/flights/${newFlight.id}`);
    } catch (err) {
      console.error("Error duplicating flight:", err);
      setError(
        err instanceof Error ? err.message : "Failed to duplicate flight",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      await deleteFlight(flightId!);
      navigate(`/campaigns/${campaignId}`);
    } catch (err) {
      console.error("Error deleting flight:", err);
      // The database explains WHY (a committed Event in a Box invitation is
      // retained as the record of what was scheduled); flattening that to
      // "Failed to delete flight" throws the useful part away.
      setError(err instanceof Error ? err.message : "Failed to delete flight");
      setShowDeleteConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setSubmitting(true);
      setError(null);
      // 2026-05-27 (Bug E): if the operator transitions to status='scheduled'
      // without a date set, refuse instead of silently defaulting to NOW.
      // The prior auto-NOW fallback caused the "Schedule sends immediately
      // after unschedule" symptom: unschedule cleared scheduled_send_at,
      // operator clicked Schedule again, status moved to scheduled with
      // scheduled_send_at=NOW, cron picked it up within 2 min and fired.
      // Callers should open the Edit Schedule modal first to pick a date.
      if (newStatus === "scheduled" && !flight?.scheduled_send_at) {
        setError(
          "Cannot schedule without a send time. Use Edit Schedule to pick a date first.",
        );
        return;
      }
      const updates: UpdateFlightDTO = {
        status: newStatus as UpdateFlightDTO["status"],
      };
      if (newStatus === "sent") {
        updates.sent_at = new Date().toISOString();
      }
      await updateFlight(flightId!, updates);
      setSuccess(`Flight status changed to ${newStatus}`);
      loadData();
    } catch (err) {
      console.error("Error changing status:", err);
      setError("Failed to change flight status");
    } finally {
      setSubmitting(false);
    }
  };

  // Send Now — fires send-campaign-flight via the user's JWT path. The
  // edge function does its own atomic status='draft' → 'sending' flip
  // and updates status to 'sent' when complete. Wall time ~30-60s for
  // a 2,990-recipient send under the 20-way concurrency change.
  const handleSendNow = async (opts?: { warnAck?: string }) => {
    if (!flight) {
      return;
    }
    try {
      setSendingNow(true);
      setSubmitting(true);
      setError(null);
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        throw new Error("Not authenticated");
      }
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-campaign-flight`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          flight_id: flight.id,
          ...(opts?.warnAck ? { warn_ack: opts.warnAck } : {}),
        }),
      });
      const body = await res.json().catch(() => ({}));
      // G3 pre-send validation. 400 blocked = hard block (invalid addresses only since migration 411);
      // 409 warn_requires_ack = advisory warn (no current reason produces one: role addresses and the
      // name-and-digits pattern are notes that never stop a send; kept for an unknown future warn reason). Both surface the Health dialog with the class-aware breakdown + remediation. The
      // gate fingerprint is the exact recipient-set hash the override/warn-ack must carry.
      if (res.status === 400 && body?.blocked === "g3_presend_validation") {
        setG3Gate({ kind: "block", context: "send", ...(body.gate ?? {}) });
        setG3OverrideReason("");
        return;
      }
      if (res.status === 409 && body?.warn_requires_ack) {
        setG3Gate({ kind: "warn", context: "send", ...(body.gate ?? {}) });
        setG3OverrideReason("");
        return;
      }
      if (!res.ok) {
        throw new Error(body?.error || `Send failed (HTTP ${res.status})`);
      }
      if (body?.phase === "email_check") {
        // The automatic NeverBounce check parked the send while addresses are checked; the
        // scheduler resumes it on its own. Not "sent (0 recipients)".
        const n = Number(body?.waiting_addresses ?? 0);
        setSuccess(
          n > 0
            ? `Checking ${n.toLocaleString()} address${n === 1 ? "" : "es"} — sending starts automatically when done.`
            : "Checking this flight's email addresses — sending starts automatically when done.",
        );
        loadData();
        return;
      }
      const sent = body?.results?.sent ?? body?.sent ?? 0;
      const failed = body?.results?.failed ?? body?.failed ?? 0;
      setSuccess(
        failed > 0
          ? `Flight sent — ${sent} succeeded, ${failed} failed. See email_tracking for details.`
          : `Flight sent successfully (${sent} recipients).`,
      );
      loadData();
    } catch (err) {
      console.error("Error sending flight now:", err);
      setError(err instanceof Error ? err.message : "Failed to send flight");
    } finally {
      setSendingNow(false);
      setSubmitting(false);
    }
  };

  const validateUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-flight-audience`;
  // What "Suppress invalid" covers: INVALID addresses only (migration 411). The name-and-digits pattern is
  // informational and is never suppressed by this action.
  const g3FlaggedCount = g3Gate?.invalid ?? 0;
  const g3Token = async () =>
    (await supabase.auth.getSession()).data.session?.access_token ?? "";

  // BLOCK "Confirm & send anyway": record a fingerprint-bound override for THIS exact snapshot, then
  // retry the send — the gate now returns ok because a matching override exists.
  const handleG3Override = async () => {
    if (!flight || !g3Gate?.fingerprint) {
      return;
    }
    if (!g3OverrideReason.trim()) {
      setError("A reason is required to send anyway.");
      return;
    }
    try {
      setG3Busy(true);
      setError(null);
      const accessToken = await g3Token();
      if (!accessToken) {
        throw new Error("Not authenticated");
      }
      const res = await fetch(validateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          flightId: flight.id,
          mode: "override",
          snapshotFingerprint: g3Gate.fingerprint,
          reason: g3OverrideReason.trim(),
          total: g3Gate.total,
          flaggedCount: g3FlaggedCount,
          flaggedRatio: g3Gate.invalid_ratio,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        throw new Error(body?.error || `Override failed (HTTP ${res.status})`);
      }
      setG3Gate(null);
      setG3OverrideReason("");
      await handleSendNow();
    } catch (err) {
      console.error("Error overriding pre-send validation:", err);
      setError(err instanceof Error ? err.message : "Failed to override");
    } finally {
      setG3Busy(false);
    }
  };

  // WARN "Continue": for a SEND, re-send carrying the gate fingerprint as the warn acknowledgement
  // (advisory — the gate proceeds because warn_ack matches the live snapshot). For a SCHEDULE, PERSIST a
  // fingerprint-bound warn-ack first (so the later cron send records it as acknowledged, not silently
  // advisory), then schedule.
  const handleG3WarnContinue = async () => {
    if (!flight || !g3Gate?.fingerprint) {
      return;
    }
    const fp = g3Gate.fingerprint;
    const ctx = g3Gate.context;
    if (ctx === "schedule") {
      try {
        setG3Busy(true);
        const accessToken = await g3Token();
        if (!accessToken) {
          throw new Error("Not authenticated");
        }
        const res = await fetch(validateUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            flightId: flight.id,
            mode: "warn_ack",
            fingerprint: fp,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body?.success) {
          throw new Error(
            body?.error || "Could not record the acknowledgement.",
          );
        }
        setG3Gate(null);
        await handleStatusChange("scheduled");
      } catch (err) {
        console.error("Error recording schedule-time warn ack:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to acknowledge the warning",
        );
      } finally {
        setG3Busy(false);
      }
    } else {
      setG3Gate(null);
      await handleSendNow({ warnAck: fp });
    }
  };

  // Schedule confirm — gate at schedule time too (D4). Preview the audience; if enforcement is on and the
  // list would block/warn, open the Health dialog (schedule context: a BLOCK must be suppressed/fixed — no
  // override at schedule time, since the fingerprint override needs a live snapshot; a WARN can ack).
  const handleScheduleConfirm = async () => {
    if (!flight) {
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const accessToken = await g3Token();
      if (!accessToken) {
        throw new Error("Not authenticated");
      }
      const pv = await fetch(validateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ flightId: flight.id, mode: "preview" }),
      });
      const pvBody = await pv.json().catch(() => ({}));
      const grade = pvBody?.grade ?? {};
      const decision = grade.decision as string | undefined;
      // The name-and-digits pattern and role addresses are informational notes only (migration 411, owner
      // 2026-10-09) — they never open this dialog, even if an older database body still reports a warning.
      const noteOnly = decision !== "block" && isG3NoteOnlyReason(grade.reason);
      if (
        pv.ok &&
        pvBody?.enforcement_enabled &&
        !noteOnly &&
        (decision === "block" || decision === "warn")
      ) {
        setG3Gate({
          kind: decision === "block" ? "block" : "warn",
          context: "schedule",
          reason: grade.reason,
          total: grade.total,
          invalid: grade.invalid,
          synthetic: grade.synthetic,
          role: grade.role,
          invalid_ratio: grade.invalid_ratio,
          synthetic_ratio: grade.synthetic_ratio,
          role_ratio: grade.role_ratio,
          fingerprint: grade.fingerprint,
        });
        setG3OverrideReason("");
        return;
      }
      // ok / enforcement off → proceed to schedule.
      await handleStatusChange("scheduled");
    } catch (err) {
      console.error("Error validating audience before schedule:", err);
      setError(
        err instanceof Error ? err.message : "Failed to validate audience",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // "Suppress invalid & send": preview the audience to get its fingerprint + invalid count, globally
  // suppress the INVALID addresses (bad format / disposable domain) bound to that fingerprint, then re-send the
  // remainder. Never suppresses name-and-digits pattern, role or valid addresses (migration 411).
  // Admin/superadmin only (enforced server-side).
  const handleG3Suppress = async () => {
    if (!flight) {
      return;
    }
    const ctx = g3Gate?.context ?? "send";
    const proceed = async () =>
      ctx === "schedule" ? handleStatusChange("scheduled") : handleSendNow();
    try {
      setG3Busy(true);
      setError(null);
      const accessToken = await g3Token();
      if (!accessToken) {
        throw new Error("Not authenticated");
      }
      const hdr = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      };
      // 1. preview → current audience fingerprint + flagged count (the suppress binds to BOTH).
      const pv = await fetch(validateUrl, {
        method: "POST",
        headers: hdr,
        body: JSON.stringify({ flightId: flight.id, mode: "preview" }),
      });
      const pvBody = await pv.json().catch(() => ({}));
      if (!pv.ok || !pvBody?.success) {
        throw new Error(pvBody?.error || "Could not preview the audience.");
      }
      const grade = pvBody.grade ?? {};
      if (grade.unsupported) {
        // dynamic segment / audience_filters — the list-only grader can't scope the suppression to the
        // actual send audience, so suppress-before-send isn't offered. The send-time snapshot gate still
        // protects the send; clean the list at its source or use Continue/override.
        throw new Error(
          "Suppress-before-send isn't available for this audience (dynamic segment or audience filters). Clean the list at its source.",
        );
      }
      const flagged = Number(grade.flagged_count ?? 0);
      if (flagged <= 0) {
        // nothing to suppress (audience already clean) — just proceed.
        setG3Gate(null);
        await proceed();
        return;
      }
      // 2. suppress bound to count + fingerprint.
      const sp = await fetch(validateUrl, {
        method: "POST",
        headers: hdr,
        body: JSON.stringify({
          flightId: flight.id,
          mode: "suppress_flagged",
          expectedCount: flagged,
          expectedFingerprint: grade.fingerprint,
        }),
      });
      const spBody = await sp.json().catch(() => ({}));
      if (!sp.ok || !spBody?.success) {
        throw new Error(spBody?.error || `Suppress failed (HTTP ${sp.status})`);
      }
      // 3. clean list → proceed (send now, or schedule).
      setG3Gate(null);
      setSuccess(
        `Suppressed ${spBody.result?.suppressed ?? flagged} flagged address(es); ${ctx === "schedule" ? "scheduling the clean list…" : "sending the clean list…"}`,
      );
      await proceed();
    } catch (err) {
      console.error("Error suppressing flagged addresses:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to suppress flagged addresses",
      );
    } finally {
      setG3Busy(false);
    }
  };

  // Open the Edit Schedule modal with the current scheduled time
  // pre-filled. The datetime-local input expects 'YYYY-MM-DDTHH:mm'
  // in the operator's LOCAL timezone — convert the UTC scheduled_send_at
  // accordingly.
  const openEditSchedule = () => {
    if (!flight?.scheduled_send_at) {
      return;
    }
    const dt = new Date(flight.scheduled_send_at);
    // Compose YYYY-MM-DDTHH:mm in local time
    const pad = (n: number) => String(n).padStart(2, "0");
    const localStr =
      dt.getFullYear() +
      "-" +
      pad(dt.getMonth() + 1) +
      "-" +
      pad(dt.getDate()) +
      "T" +
      pad(dt.getHours()) +
      ":" +
      pad(dt.getMinutes());
    setEditScheduleAt(localStr);
    setShowEditScheduleModal(true);
  };

  const handleSaveSchedule = async () => {
    if (!flight) {
      return;
    }
    // 2026-05-27 (Bug A): when the operator clears the date input and
    // clicks Save, treat that as "Clear schedule" (unschedule) instead
    // of a silent no-op. Prior code refused empty input at the top of
    // this function AND disabled the Save button, leaving the operator
    // with no working "clear" action from this modal.
    if (!editScheduleAt) {
      await handleCancelSchedule();
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const newIso = new Date(editScheduleAt).toISOString();
      // 2026-05-27 (Bug E continuation): if we came from the Schedule
      // button on a date-less draft flight, the new date alone is
      // enough to satisfy handleStatusChange's gate. So persist date
      // AND transition status in the same write — no separate Schedule
      // click needed.
      const willChainPreSend = openPreSendAfterScheduleSave;
      await updateFlight(flightId!, {
        scheduled_send_at: newIso,
      } as UpdateFlightDTO);
      setSuccess("Schedule updated");
      setShowEditScheduleModal(false);
      if (willChainPreSend) {
        // Reset before opening PreSend so a later Edit doesn't trigger
        // an unintended chain.
        setOpenPreSendAfterScheduleSave(false);
        // Load fresh data so PreSendConfirmationModal sees the newly
        // saved scheduled_send_at, not the stale null from state.
        await loadData();
        setShowPreSendModal(true);
      } else {
        loadData();
      }
    } catch (err) {
      console.error("Error updating schedule:", err);
      setError("Failed to update schedule");
    } finally {
      setSubmitting(false);
    }
  };

  // 2026-05-27 (Bug E): Schedule button on a draft flight. If the
  // flight already has a scheduled_send_at (e.g., it had one but the
  // operator just clicked Schedule from the draft view), proceed
  // directly to PreSend. Otherwise force the Edit Schedule flow first
  // so the operator must consciously pick a future date; the PreSend
  // modal then opens automatically after they save.
  const handleScheduleClick = () => {
    if (flight?.scheduled_send_at) {
      setShowPreSendModal(true);
    } else {
      setEditScheduleAt(""); // force conscious entry
      setOpenPreSendAfterScheduleSave(true);
      setShowEditScheduleModal(true);
    }
  };

  const handleCancelSchedule = async () => {
    if (!flight) {
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      // An Event in a Box invitation cannot go back to draft: its audience,
      // design and schedule were fixed by the guarded launch, and the database
      // permits exactly one operator transition — scheduled → cancelled, with
      // no other field changed. Sending it back to draft (which is right for an
      // ordinary flight) is refused, and the invitation would still send.
      if (flight.eib_launch_id) {
        await updateFlight(flightId!, {
          status: "cancelled",
        } as UpdateFlightDTO);
        setSuccess(
          "Invitation cancelled — it will not send. Event setup is unchanged; re-run it to send a new invitation.",
        );
      } else {
        await updateFlight(flightId!, {
          status: "draft",
          scheduled_send_at: null,
        } as UpdateFlightDTO);
        setSuccess("Schedule cancelled — flight returned to draft");
      }
      setShowCancelScheduleConfirm(false);
      // If the operator triggered cancel from inside the edit form,
      // close it so they see the now-draft detail view rather than an
      // edit form with a stale "scheduled" notice.
      setIsEditing(false);
      loadData();
    } catch (err) {
      console.error("Error cancelling schedule:", err);
      setError("Failed to cancel schedule");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreviewEmail = async () => {
    const emailTemplateId = flight?.email_template_id;
    if (!emailTemplateId) {
      return;
    }

    setLoadingPreview(true);
    try {
      const details = await getSendGridTemplateDetails(emailTemplateId);
      setTemplateDetails(details);
      setShowPreview(true);
    } catch (err: any) {
      console.error("Error loading template preview:", err);
      const msg = err?.message?.includes("not found")
        ? "Template not found in SendGrid. It may have been deleted or the ID is invalid."
        : "Failed to load email template preview";
      setError(msg);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Detect which channels are configured
  const hasEmail = !!(
    flight?.email_template_id || (flight?.emails_sent ?? 0) > 0
  );
  const hasSMS = !!(flight?.sms_template_id || (flight?.sms_sent ?? 0) > 0);
  const hasVouchers = !!(
    flight?.template_id || (flight?.vouchers_generated ?? 0) > 0
  );

  // Voucher health (surfaces the generate-fn's metadata.voucher_generation flag + a
  // list/voucher staleness check). Reminders (voucher_source_flight_id) reuse the source's
  // vouchers and don't generate, so these warnings don't apply to them.
  const voucherHealth = (() => {
    const f = flight as
      | {
          generate_vouchers?: boolean;
          voucher_source_flight_id?: string | null;
          list_id?: string | null;
          vouchers_generated?: number | null;
          metadata?: Record<string, unknown> | null;
        }
      | undefined;
    if (!f || f.voucher_source_flight_id || !f.generate_vouchers) {
      return null;
    }
    const vg = (f.metadata?.voucher_generation ?? null) as {
      ok?: boolean;
      processed?: number;
      vouchers_total?: number;
      missing_count?: number;
      list_id?: string | null;
    } | null;
    // Fix B: generation reported incomplete (per-recipient errors or fewer vouchers than processed).
    const incomplete = !!vg && vg.ok === false;
    const missing = vg?.missing_count ?? 0;
    // Fix D: vouchers were generated against a DIFFERENT list than the flight's current one
    // (e.g. the list was changed after generation). Only meaningful once some exist.
    const stale =
      !!vg &&
      !!vg.list_id &&
      !!f.list_id &&
      vg.list_id !== f.list_id &&
      (f.vouchers_generated ?? 0) > 0;
    if (!incomplete && !stale) {
      return null;
    }
    return {
      incomplete,
      missing,
      stale,
      generatedForList: vg?.list_id ?? null,
    };
  })();

  const handleRegenerateVouchers = async () => {
    if (!flightId) {
      return;
    }
    setRegeneratingVouchers(true);
    setError(null);
    setSuccess(null);
    try {
      // Idempotent: fills missing vouchers for the current list and refreshes the
      // metadata.voucher_generation flag (clears the incomplete/stale banner on success).
      await generateFlightVouchers(flightId);
      await loadData();
      setSuccess("Voucher generation re-run for the current segment.");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to regenerate vouchers",
      );
    } finally {
      setRegeneratingVouchers(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: "bg-gray-100 text-gray-800",
      scheduled: "bg-yellow-100 text-yellow-800",
      sending: "bg-blue-100 text-blue-800",
      sent: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      failed: "bg-red-100 text-red-800",
    };

    const option = FLIGHT_STATUS_OPTIONS.find((o) => o.value === status);

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100 text-gray-800"}`}
      >
        {option?.label || status}
      </span>
    );
  };

  const formatDate = (date?: string) => {
    if (!date) {
      return "N/A";
    }
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) {
        return "N/A";
      }
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const pct = (num: number, den: number) =>
    den > 0 ? `${Math.round((num / den) * 100)}%` : "—";

  const isRecipientTableFlight =
    Boolean(
      (flight?.metadata as Record<string, unknown> | undefined)?.send_mode ===
        "recipient_table",
    ) ||
    Boolean(flight?.send_recipients_snapshot_complete_at) ||
    Boolean(flight?.send_snapshot_progress) ||
    Boolean(recipientProgress && recipientProgress.total > 0);
  const snapshotSizeEstimate =
    typeof flight?.send_snapshot_progress?.total_size_estimate === "number"
      ? flight.send_snapshot_progress.total_size_estimate
      : null;

  // 2026-06-08: when a `failed` parent flight was rescued via the
  // manual_split_into_N_subflights pattern, the parent's stat
  // counters only reflect its own sliver (what it managed to send
  // before the original failure). The send was completed via the
  // sub-flights, so the only honest representation is the combined
  // parent + splits stats. Otherwise Recipients=0 / Sent=2040 looks
  // like the campaign barely shipped, when in reality every
  // recipient on the eligible audience got the email.
  const isRecoveredParent = recoverySplits.length > 0;
  const splitTotals = isRecoveredParent
    ? recoverySplits.reduce(
        (acc, s) => ({
          recipients: acc.recipients + (s.total_recipients ?? 0),
          sent: acc.sent + (s.emails_sent ?? 0),
          delivered: acc.delivered + (s.emails_delivered ?? 0),
          opened: acc.opened + (s.emails_opened ?? 0),
          clicked: acc.clicked + (s.emails_clicked ?? 0),
          bounced: acc.bounced + (s.emails_bounced ?? 0),
          deferred: acc.deferred + (s.emails_deferred ?? 0),
        }),
        {
          recipients: 0,
          sent: 0,
          delivered: 0,
          opened: 0,
          clicked: 0,
          bounced: 0,
          deferred: 0,
        },
      )
    : null;
  // Use metadata.stuck_partial_send.total_eligible_audience as the
  // recipients count when present — that's the original audience
  // size before the send-time split, and is the most-honest
  // denominator for delivery/open/click rates on the parent page.
  const totalEligibleAudience = isRecoveredParent
    ? (((
        (flight?.metadata as Record<string, unknown> | undefined)?.[
          "stuck_partial_send"
        ] as Record<string, unknown> | undefined
      )?.["total_eligible_audience"] as number | undefined) ?? null)
    : null;

  const displayRecipients = isRecoveredParent
    ? (totalEligibleAudience ??
      (flight?.total_recipients ?? 0) + (splitTotals?.recipients ?? 0))
    : isRecipientTableFlight && recipientProgress
      ? recipientProgress.total || snapshotSizeEstimate || 0
      : (flight?.total_recipients ?? 0);
  const displayEmailSent = isRecoveredParent
    ? (flight?.emails_sent ?? 0) + (splitTotals?.sent ?? 0)
    : isRecipientTableFlight && recipientProgress
      ? recipientProgress.sent
      : (flight?.emails_sent ?? 0);
  const displayDelivered = isRecoveredParent
    ? (flight?.emails_delivered ?? 0) + (splitTotals?.delivered ?? 0)
    : (flight?.emails_delivered ?? 0);
  const displayOpened = isRecoveredParent
    ? (flight?.emails_opened ?? 0) + (splitTotals?.opened ?? 0)
    : (flight?.emails_opened ?? 0);
  const displayClicked = isRecoveredParent
    ? (flight?.emails_clicked ?? 0) + (splitTotals?.clicked ?? 0)
    : (flight?.emails_clicked ?? 0);
  const displayBounced = isRecoveredParent
    ? (flight?.emails_bounced ?? 0) + (splitTotals?.bounced ?? 0)
    : (flight?.emails_bounced ?? 0);
  // Deferral-visibility: recipients currently deferred/throttled by the receiving provider
  // (accepted by SendGrid, no delivered-or-bounced result yet). Rollup-maintained on
  // campaign_flights.emails_deferred; self-corrects to 0 as each resolves. This is the
  // largest, most-actionable slice of "pending" — surfaced so operators know a pile-up on
  // one provider is a deliverability/throttling matter, not a stuck platform.
  const displayDeferred = isRecoveredParent
    ? (flight?.emails_deferred ?? 0) + (splitTotals?.deferred ?? 0)
    : (flight?.emails_deferred ?? 0);
  const displayFailed =
    isRecipientTableFlight && recipientProgress ? recipientProgress.failed : 0;
  const displayPending =
    isRecipientTableFlight && recipientProgress ? recipientProgress.pending : 0;
  const displayClaimed =
    isRecipientTableFlight && recipientProgress ? recipientProgress.claimed : 0;
  const displayUnconfirmed =
    isRecipientTableFlight && recipientProgress
      ? recipientProgress.unconfirmed
      : 0;
  const displaySkipped =
    isRecipientTableFlight && recipientProgress ? recipientProgress.skipped : 0;

  // Pre-send audience accounting (frozen at finalize in metadata.audience_exclusions).
  // The Email Performance numbers below are the ELIGIBLE send set; this reconciles them
  // back to the full source list so operators see who was excluded before send.
  const audienceExclusions = (
    flight?.metadata as Record<string, unknown> | undefined
  )?.["audience_exclusions"] as
    | {
        raw_audience_count?: number;
        eligible_count?: number;
        excluded_total?: number;
      }
    | undefined;
  const rawAudienceCount = audienceExclusions?.raw_audience_count;
  const eligibleCount = audienceExclusions?.eligible_count;
  const excludedBeforeSend = audienceExclusions?.excluded_total;
  const hasAudienceAccounting =
    typeof rawAudienceCount === "number" && typeof eligibleCount === "number";

  const hasActiveLease = Boolean(
    flight?.lease_id &&
      flight.lease_expires_at &&
      new Date(flight.lease_expires_at).getTime() > Date.now(),
  );
  const latestSendProgressAt = recipientProgress?.latest_progress_at
    ? new Date(recipientProgress.latest_progress_at).getTime()
    : flight?.updated_at
      ? new Date(flight.updated_at).getTime()
      : NaN;
  const minutesSinceSendProgress = Number.isFinite(latestSendProgressAt)
    ? (Date.now() - latestSendProgressAt) / 60000
    : null;
  const stuckCheck = (
    flight?.metadata as Record<string, unknown> | undefined
  )?.["stuck_check"] as Record<string, unknown> | undefined;
  // Migration 2026-10-10-413: an Event in a Box REMINDER blocked by the pre-send address check is HELD in place
  // (status 'sending', list kept, lease released) while dispatch-eib-reminders re-checks it about every five minutes. That is a
  // deliberate wait, not a stuck send -- show the calm held banner, never the "Send appears stuck" one.
  const presendHold = (
    flight?.metadata as Record<string, unknown> | undefined
  )?.["g3_presend_validation"] as
    | {
        held?: unknown;
        blocked_at?: unknown;
        gate?: { invalid?: unknown; total?: unknown };
      }
    | undefined;
  const isPresendHeld =
    flight?.status === "sending" &&
    flight?.eib_flight_role === "invite_reminder" &&
    presendHold?.held === true;
  const showStuckWarning =
    !isPresendHeld &&
    flight?.status === "sending" &&
    Boolean(stuckCheck) &&
    (!isRecipientTableFlight ||
      !recipientProgress ||
      (displayPending > 0 &&
        displayClaimed === 0 &&
        !hasActiveLease &&
        minutesSinceSendProgress !== null &&
        minutesSinceSendProgress > 15));

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
          <p className="text-gray-600 mt-4">Loading flight...</p>
        </div>
      </div>
    );
  }

  if (error && !flight) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || "Flight not found"}
        </div>
        <button
          onClick={() => navigate(backTo || `/campaigns/${campaignId}`)}
          className="mt-4 px-4 py-2 text-primary-600 hover:text-primary-700"
        >
          {backTo ? "← Back to Contact" : "← Back to Campaign"}
        </button>
      </div>
    );
  }

  if (!flight || !campaign) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Flight not found
        </div>
        <button
          onClick={() => navigate(backTo || `/campaigns/${campaignId}`)}
          className="mt-4 px-4 py-2 text-primary-600 hover:text-primary-700"
        >
          {backTo ? "← Back to Contact" : "← Back to Campaign"}
        </button>
      </div>
    );
  }

  // Migration 203: a direct link to a flight of a soft-deleted campaign shows the deleted state, not the flight's
  // send controls.
  if (!isCampaignVisible(campaign)) {
    return (
      <DeletedCampaignNotice
        campaign={campaign}
        onBack={() => navigate("/campaigns")}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="mb-6">
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Link
            to="/campaigns"
            className="hover:text-primary-600 transition-colors"
          >
            Campaigns
          </Link>
          <span>/</span>
          <Link
            to={`/campaigns/${campaignId}`}
            className="hover:text-primary-600 transition-colors"
          >
            {campaign.name}
          </Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">{flight.name}</span>
        </nav>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-900 hover:text-red-700"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center justify-between">
            <span>{success}</span>
            <button
              onClick={() => setSuccess(null)}
              className="text-green-900 hover:text-green-700"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Missing-audience-list warning. Fires when ALL of:
              - flight has a list_id set, AND
              - the FK join hydrated a `list` property (i.e. we have a
                definitive answer about whether the row exists), AND
              - that hydrated list is null (FK target missing).
            Before 2026-05-11 this checked `!(flight as any).list` which
            also fired when the property was simply undefined (the old
            getFlight() didn't hydrate the relation). That made the
            warning permanent for every healthy flight. */}
        {flight.list_id &&
          "list" in flight &&
          (flight as { list: unknown }).list === null && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
              <svg
                className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M19 19l-7-14-7 14"
                />
              </svg>
              <div className="text-sm text-red-900">
                <span className="font-semibold">Audience list deleted.</span>{" "}
                This flight references a list (id{" "}
                <code className="font-mono text-xs">{flight.list_id}</code>)
                that no longer exists. The flight cannot be sent until you
                assign a current list.
              </div>
            </div>
          )}

        {/* Stuck-flight warning. Fires when the pg_cron'd
            `mark_potentially_stuck_flights` function has flagged this
            flight's metadata.stuck_check because status='sending' has
            persisted without updated_at progress for >15 minutes.

            P1 (2026-06-02) ships visibility only — the marker is
            informational, status is NOT auto-mutated to 'failed'. P2
            ships the recipient-table + lease infrastructure that
            replaces this with a proper Resume Recovery flow.

            Until then: operators should NOT manually re-send a
            stuck flight (could re-send to recipients already sent).
            Use the SQL recovery pattern in
            /tmp/option_a_restart_flight.sql or the manual-split
            pattern in /tmp/path1_manual_split.sql. */}
        {isPresendHeld && (
          <div
            className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-2"
            data-testid="presend-held-banner"
          >
            <svg
              className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm text-blue-900 flex-1">
              <div className="font-semibold">
                Held by the pre-send check — waiting.
              </div>
              <div className="mt-1">
                {typeof presendHold?.gate?.invalid === "number" &&
                typeof presendHold?.gate?.total === "number"
                  ? `${presendHold.gate.invalid} of ${presendHold.gate.total} addresses look invalid, so this reminder is on hold. `
                  : "Too many addresses look invalid, so this reminder is on hold. "}
                It is re-checked about every five minutes and is skipped if not
                cleared before the send window closes.
              </div>
            </div>
          </div>
        )}

        {showStuckWarning && (
          <div className="mb-4 bg-amber-50 border border-amber-300 rounded-md p-3 flex items-start gap-2">
            <svg
              className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M21 19a2 2 0 01-1.732 1H4.732A2 2 0 013 19l8.732-15a1 1 0 011.732 0L21 19z"
              />
            </svg>
            <div className="text-sm text-amber-900 flex-1">
              <div className="font-semibold">
                Send appears stuck —{" "}
                {(() => {
                  const mins = stuckCheck?.["duration_minutes"];
                  return typeof mins === "number"
                    ? `${Math.round(mins)} min`
                    : "duration unknown";
                })()}{" "}
                with no progress.
              </div>
              <div className="mt-1">
                This flight is at status <code>sending</code> but has not
                updated in &gt; 15 minutes — the send process likely hit a
                platform limit (e.g., <code>CPU Time exceeded</code>) and was
                killed mid-send. Some recipients may have already received the
                email.
              </div>
              <div className="mt-2 font-semibold">
                Do NOT click Send Now or re-fire — that would re-send to
                recipients already delivered. Contact an engineer to run the
                recovery procedure (clone with derived list excluding
                already-sent contacts). See{" "}
                <code>
                  docs/AUDIT_PROMPT_FLIGHT_SEND_RECIPIENT_TABLE_AND_LEASES.md
                </code>
                .
              </div>
            </div>
          </div>
        )}

        {/* 2026-06-08: recovered-via-splits callout.
            When a `failed` parent flight was rescued via the
            manual_split_into_N_subflights pattern, the alarming
            red "Send failed" banner is misleading because the send
            was completed via sub-flights. Render a green callout
            instead with the recovery context + links to the splits.

            Detection: metadata.stuck_partial_send.recovery_split_count > 0
            (set by the operator when initiating the recovery).
        */}
        {flight.status === "failed" &&
          recoverySplits.length > 0 &&
          (() => {
            const stuckPartial = (
              flight.metadata as Record<string, unknown> | undefined
            )?.["stuck_partial_send"] as Record<string, unknown> | undefined;
            const splitSentTotal = recoverySplits.reduce(
              (acc, s) => acc + (s.emails_sent ?? 0),
              0,
            );
            const splitDeliveredTotal = recoverySplits.reduce(
              (acc, s) => acc + (s.emails_delivered ?? 0),
              0,
            );
            const parentSent = flight.emails_sent ?? 0;
            const parentDelivered = flight.emails_delivered ?? 0;
            const totalAudience =
              (stuckPartial?.["total_eligible_audience"] as
                | number
                | undefined) ?? null;
            const operatorResolutionKey = Object.keys(
              (flight.metadata as Record<string, unknown> | undefined) ?? {},
            ).find((k) => k.startsWith("operator_resolution_"));
            const operatorResolution = operatorResolutionKey
              ? ((flight.metadata as Record<string, unknown>)[
                  operatorResolutionKey
                ] as string)
              : null;
            return (
              <div className="mb-4 bg-green-50 border border-green-300 rounded-md p-3 flex items-start gap-2">
                <svg
                  className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="text-sm text-green-900 flex-1">
                  <div className="font-semibold">
                    Send completed via recovery sub-flights.
                  </div>
                  <div className="mt-1">
                    The original send job ended early
                    {parentSent > 0
                      ? ` after delivering to ${parentSent.toLocaleString()} of the audience`
                      : ""}
                    . An operator finished the run via{" "}
                    <strong>
                      {recoverySplits.length} split sub-flight
                      {recoverySplits.length === 1 ? "" : "s"}
                    </strong>
                    {totalAudience
                      ? ` covering the remaining ${(
                          totalAudience - parentSent
                        ).toLocaleString()} recipients`
                      : ""}
                    . Every recipient on the eligible audience has been sent.
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="font-semibold">Parent sent: </span>
                      {parentSent.toLocaleString()}
                    </div>
                    <div>
                      <span className="font-semibold">Splits sent: </span>
                      {splitSentTotal.toLocaleString()}
                    </div>
                    <div>
                      <span className="font-semibold">Total sent: </span>
                      {(parentSent + splitSentTotal).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-semibold">Total delivered: </span>
                      {(parentDelivered + splitDeliveredTotal).toLocaleString()}
                    </div>
                  </div>
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs font-semibold text-green-800 hover:text-green-900">
                      Show {recoverySplits.length} recovery sub-flight
                      {recoverySplits.length === 1 ? "" : "s"}
                    </summary>
                    <div className="mt-2 max-h-64 overflow-y-auto rounded border border-green-200 bg-white">
                      <table className="min-w-full text-xs">
                        <thead className="bg-green-50 text-left">
                          <tr>
                            <th className="px-2 py-1.5 font-semibold">Name</th>
                            <th className="px-2 py-1.5 font-semibold">
                              Status
                            </th>
                            <th className="px-2 py-1.5 font-semibold text-right">
                              Sent
                            </th>
                            <th className="px-2 py-1.5 font-semibold text-right">
                              Delivered
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-green-100">
                          {recoverySplits.map((s) => (
                            <tr key={s.id} className="hover:bg-green-50/60">
                              <td className="px-2 py-1.5">
                                <Link
                                  to={`/campaigns/${flight.campaign_id}/flights/${s.id}`}
                                  className="text-primary-600 hover:underline"
                                >
                                  {s.name.replace(`${flight.name} — `, "")}
                                </Link>
                              </td>
                              <td className="px-2 py-1.5">{s.status}</td>
                              <td className="px-2 py-1.5 text-right">
                                {(s.emails_sent ?? 0).toLocaleString()}
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                {(s.emails_delivered ?? 0).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                  {operatorResolution && (
                    <div className="mt-2 text-xs italic text-green-800">
                      Operator note: {operatorResolution}
                    </div>
                  )}
                  <div className="mt-2 font-semibold text-green-900">
                    Do NOT click Send Now — this would re-send to recipients who
                    already got the email. The parent flight stays in status
                    `failed` as the historical record of the original incident.
                  </div>
                </div>
              </div>
            );
          })()}

        {/* P2.1: terminal-failed flight callout.
            Set by finalize_flight() or send-campaign-flight's outer catch
            when the send pipeline gave up before all recipients reached a
            terminal state. metadata.final_send_stats has the breakdown.
            Gated on `recoverySplits.length === 0` — when recovery splits
            exist, the green "recovered" banner above replaces this one. */}
        {flight.status === "failed" && recoverySplits.length === 0 && (
          <div className="mb-4 bg-red-50 border border-red-300 rounded-md p-3 flex items-start gap-2">
            <svg
              className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M21 19a2 2 0 01-1.732 1H4.732A2 2 0 013 19l8.732-15a1 1 0 011.732 0L21 19z"
              />
            </svg>
            <div className="text-sm text-red-900 flex-1">
              <div className="font-semibold">
                Send failed before completion.
              </div>
              <div className="mt-1">
                The send pipeline terminated before all recipients reached a
                terminal state. Some recipients may have received the email and
                others did not. Final state breakdown is in the metadata:
              </div>
              {(() => {
                const stats = (
                  flight.metadata as Record<string, unknown> | undefined
                )?.["final_send_stats"] as Record<string, number> | undefined;
                if (!stats) {
                  return null;
                }
                return (
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                    <div>
                      <span className="font-semibold">Sent: </span>
                      {stats.sent ?? 0}
                    </div>
                    <div>
                      <span className="font-semibold">Failed: </span>
                      {stats.failed ?? 0}
                    </div>
                    <div>
                      <span className="font-semibold">Skipped: </span>
                      {stats.skipped ?? 0}
                    </div>
                    <div>
                      <span className="font-semibold">Unconfirmed: </span>
                      {stats.unconfirmed ?? 0}
                    </div>
                    <div>
                      <span className="font-semibold">Total: </span>
                      {stats.total ?? 0}
                    </div>
                  </div>
                );
              })()}
              <div className="mt-2 font-semibold">
                Do NOT click Send Now or re-fire. Contact an engineer to run the
                recovery procedure (clone with derived list excluding
                already-sent contacts).
              </div>
            </div>
          </div>
        )}

        {/* P2.1: 'sent' flight with unconfirmed recipients.
            Set by finalize_flight() when one or more recipient rows are
            stuck at status='unconfirmed' (sendOne returned ok but the
            corresponding UPDATE never happened — likely a CPU SIGKILL
            mid-recipient). The send is technically complete but the count
            is not clean; reconciliation against SendGrid is recommended. */}
        {flight.status === "sent" &&
          (flight.metadata as Record<string, unknown> | undefined)?.[
            "send_status_warning"
          ] === "has_unconfirmed_recipients" && (
            <div className="mb-4 bg-amber-50 border border-amber-300 rounded-md p-3 flex items-start gap-2">
              <svg
                className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 19a2 2 0 01-1.732 1H4.732A2 2 0 013 19l8.732-15a1 1 0 011.732 0L21 19z"
                />
              </svg>
              <div className="text-sm text-amber-900 flex-1">
                <div className="font-semibold">
                  Send completed with unconfirmed recipients.
                </div>
                <div className="mt-1">
                  {(() => {
                    const stats = (
                      flight.metadata as Record<string, unknown> | undefined
                    )?.["final_send_stats"] as
                      | Record<string, number>
                      | undefined;
                    const unconfirmed = stats?.unconfirmed ?? 0;
                    const total = stats?.total ?? 0;
                    return (
                      <>
                        {unconfirmed} of {total} recipients are in an{" "}
                        <code>unconfirmed</code> state. Each of these had a
                        send-attempt row inserted before the worker died,
                        meaning SendGrid was likely called but the success
                        UPDATE was not recorded. The actual send count may
                        exceed the reported count, by at most the unconfirmed
                        count. (Recipients claimed by a dead worker that did NOT
                        reach the attempt-row step are automatically requeued
                        and not counted here.)
                      </>
                    );
                  })()}
                </div>
                <div className="mt-2">
                  Webhook events from SendGrid (P2.2) will reconcile these rows.
                  For now, treat the displayed "sent" count as a lower bound.
                </div>
              </div>
            </div>
          )}

        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">{flight.name}</h1>
            {getStatusBadge(flight.status)}
          </div>
          <div className="flex items-center gap-2">
            {(flight.status === "draft" || flight.status === "scheduled") &&
              flight.email_template_id && (
                <button
                  onClick={() => setShowTestSendModal(true)}
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 disabled:opacity-50"
                  title="Send a test render of this flight to a recipient you choose"
                >
                  Send Test
                </button>
              )}
            {flight.status === "draft" && (
              <>
                <button
                  onClick={() => setShowSendNowModal(true)}
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm bg-green-100 text-green-800 rounded-lg hover:bg-green-200 disabled:opacity-50"
                  title="Send this flight right now to its target audience"
                >
                  Send Now
                </button>
                <button
                  onClick={handleScheduleClick}
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 disabled:opacity-50"
                  title={
                    flight.scheduled_send_at
                      ? "Open confirmation and schedule this flight"
                      : "Pick a send date — confirmation opens automatically after you save"
                  }
                >
                  Schedule
                </button>
              </>
            )}
            {flight.status === "scheduled" && (
              <>
                <button
                  onClick={openEditSchedule}
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 disabled:opacity-50"
                  title="Change when this flight will send"
                >
                  Edit Schedule
                </button>
                <button
                  onClick={() => setShowCancelScheduleConfirm(true)}
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  title={
                    flight.eib_launch_id
                      ? "Cancel this invitation so it does not send"
                      : "Cancel the scheduled send and return this flight to draft"
                  }
                >
                  Cancel Schedule
                </button>
              </>
            )}
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              title="Edit flight"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
            {!flight.eib_launch_id && (
              <button
                onClick={handleDuplicate}
                disabled={submitting}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Duplicate this flight (as draft in same campaign)"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            )}
            {!flight.eib_launch_id && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete flight"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
            {flight.eib_launch_id && (
              <button
                type="button"
                disabled={exportingRecipients}
                title="Export the targeted recipient list (first/last/email/phone + registered Y/N, current as of export) — useful when invitation/reminder texts are sent outside the platform."
                onClick={async () => {
                  setExportingRecipients(true);
                  setError(null);
                  setSuccess(null);
                  try {
                    const r = await downloadEibRecipientsCsv(
                      flight.eib_launch_id!,
                    );
                    setSuccess(
                      `Exported ${r.rowCount} recipient${
                        r.rowCount === 1 ? "" : "s"
                      }${r.truncated ? " (list truncated at the export cap)" : ""}.`,
                    );
                  } catch (e) {
                    setError(
                      e instanceof Error
                        ? e.message
                        : "Recipient export failed",
                    );
                  } finally {
                    setExportingRecipients(false);
                  }
                }}
                className="px-3 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {exportingRecipients ? "Exporting…" : "Export recipients (CSV)"}
              </button>
            )}
            {flight.eib_launch_id && (
              <span
                className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-lg"
                title="Created by Event in a Box. Its audience and design were fixed at launch, so it is kept as the record of what was scheduled."
              >
                Event in a Box — record retained
              </span>
            )}
          </div>
        </div>

        {flight.description && (
          <p className="text-gray-600 mt-2">{flight.description}</p>
        )}
      </div>

      {/* Edit mode */}
      {isEditing ? (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Flight</h2>
          <FlightForm
            campaignId={campaignId!}
            flight={flight}
            siblingFlights={siblingFlights}
            onSubmit={handleUpdate}
            onCancel={() => setIsEditing(false)}
            onRequestCancelSchedule={
              flight.status === "scheduled"
                ? () => setShowCancelScheduleConfirm(true)
                : undefined
            }
            loading={submitting}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Email Performance — only if email channel configured */}
          {hasEmail && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Email Performance
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Among eligible recipients
                    {hasAudienceAccounting && (
                      <>
                        {" — "}
                        <span className="font-medium text-gray-700">
                          {rawAudienceCount!.toLocaleString()}
                        </span>{" "}
                        in source list ·{" "}
                        <span className="font-medium text-gray-700">
                          {eligibleCount!.toLocaleString()}
                        </span>{" "}
                        eligible/sent
                        {typeof excludedBeforeSend === "number" &&
                          excludedBeforeSend > 0 && (
                            <>
                              {" · "}
                              <span className="font-medium text-amber-700">
                                {excludedBeforeSend.toLocaleString()}
                              </span>{" "}
                              excluded before send
                            </>
                          )}
                      </>
                    )}
                  </p>
                </div>
                {flight.email_template_id && (
                  <button
                    onClick={handlePreviewEmail}
                    disabled={loadingPreview}
                    className="px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 disabled:opacity-50 flex items-center gap-1"
                  >
                    {loadingPreview ? (
                      <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                    Preview Email
                  </button>
                )}
              </div>
              {/* Delivery IP pool — visible after save and after send, so the routing a flight
                  actually used is on the record rather than only in the form. */}
              <div className="px-6 pt-4 space-y-1">
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">
                    Delivery IP pool
                  </span>
                  <span className="ml-2 text-sm text-gray-900">
                    {(() => {
                      const pool = resolvePoolDisplay(
                        (
                          flight as {
                            sendgrid_ip_pool_key?: string | null;
                          } | null
                        )?.sendgrid_ip_pool_key,
                      );
                      return pool.isWarmup ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs">
                            {pool.label}
                          </span>
                          {pool.detail && (
                            <span className="text-xs text-gray-500">
                              {pool.detail}
                            </span>
                          )}
                        </span>
                      ) : (
                        pool.label
                      );
                    })()}
                  </span>
                </div>
                {/* The categories SendGrid will receive: the pool's own plus the campaign's
                    reporting category. Shown together because neither alone is what gets sent. */}
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">
                    SendGrid categories
                  </span>
                  <span className="ml-2 text-sm text-gray-900">
                    {(() => {
                      const cats = effectiveSendCategories(
                        (
                          flight as {
                            sendgrid_ip_pool_key?: string | null;
                          } | null
                        )?.sendgrid_ip_pool_key,
                        (
                          flight as {
                            campaign?: {
                              sendgrid_category?: string | null;
                            } | null;
                          } | null
                        )?.campaign?.sendgrid_category,
                      );
                      return cats.length === 0 ? (
                        <span className="text-gray-500">None</span>
                      ) : (
                        <span className="inline-flex flex-wrap items-center gap-1">
                          {cats.map((c) => (
                            <span
                              key={c}
                              className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200 text-xs font-mono"
                            >
                              {c}
                            </span>
                          ))}
                        </span>
                      );
                    })()}
                  </span>
                </div>
              </div>

              <div className="p-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Recipients
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {displayRecipients}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Sent
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {displayEmailSent}
                  </div>
                </div>
                {/* 2026-05-27 (Item C): Delivered metric. Sourced from
                    campaign_flights.emails_delivered (denormalized count
                    populated by SendGrid delivered webhooks). Counts the
                    subset of Sent that SendGrid confirmed reached the
                    inbox provider — i.e., didn't bounce in flight. */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Delivered
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {displayDelivered}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      ({pct(displayDelivered, displayEmailSent)})
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Opened
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {displayOpened}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      ({pct(displayOpened, displayEmailSent)})
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Clicked
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {displayClicked}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      ({pct(displayClicked, displayEmailSent)})
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Bounced
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {displayBounced}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      ({pct(displayBounced, displayEmailSent)})
                    </span>
                  </div>
                </div>
                {/* Deferral-visibility: only surfaced when the flight actually has mail
                    currently deferred/throttled by the receiving provider — an exception
                    state, so it stays hidden on the clean 99% of sends. */}
                {displayDeferred > 0 && (
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <div className="text-xs text-amber-700 uppercase tracking-wide">
                      Deferred / Throttled
                    </div>
                    <div className="text-2xl font-bold text-amber-900 mt-1">
                      {displayDeferred}{" "}
                      <span className="text-sm font-normal text-amber-600">
                        ({pct(displayDeferred, displayEmailSent)})
                      </span>
                    </div>
                  </div>
                )}
                {isRecoveredParent && (
                  <div className="bg-green-50 rounded-lg p-4 md:col-span-5 border border-green-200">
                    <div className="text-xs text-green-700 uppercase tracking-wide">
                      Combined parent + {recoverySplits.length} recovery
                      sub-flight{recoverySplits.length === 1 ? "" : "s"}
                    </div>
                    <div className="mt-1 text-sm text-green-900">
                      Stats above aggregate this parent's own sends (
                      {(flight.emails_sent ?? 0).toLocaleString()}) with all
                      sub-flights' totals (
                      {(splitTotals?.sent ?? 0).toLocaleString()}
                      ). Open / click / bounce rates use combined Sent as the
                      denominator.
                    </div>
                  </div>
                )}
                {isRecipientTableFlight && recipientProgress && (
                  <div className="bg-blue-50 rounded-lg p-4 md:col-span-5">
                    <div className="text-xs text-blue-700 uppercase tracking-wide">
                      Batch send progress
                    </div>
                    <div className="mt-1 text-sm text-blue-900">
                      {displayPending} pending
                      {displayClaimed > 0 ? `, ${displayClaimed} claimed` : ""}
                      {displayFailed > 0 ? `, ${displayFailed} failed` : ""}
                      {displaySkipped > 0 ? `, ${displaySkipped} skipped` : ""}
                      {displayUnconfirmed > 0
                        ? `, ${displayUnconfirmed} unconfirmed`
                        : ""}
                      {recipientProgress.latest_progress_at
                        ? ` • Last progress ${formatDate(
                            recipientProgress.latest_progress_at,
                          )}`
                        : ""}
                    </div>
                  </div>
                )}
                {/* Deferral-visibility panel: explains WHY mail is pending (the receiving
                    provider is throttling us) + shows the reason / provider concentration so
                    the operator can act (it's a deliverability/IP matter, not a bad list). */}
                {displayDeferred > 0 &&
                  (() => {
                    const topProvider = deferralSummary?.top_providers?.[0];
                    // Concentration is computed against the summary's OWN total (same live query
                    // as the provider counts) — not displayDeferred, which is a rollup-maintained,
                    // eventually-consistent value that also folds in recovery-split flights the
                    // parent-scoped summary doesn't cover. Dividing by summaryTotal keeps the % in
                    // [0,100] and internally consistent with the breakdown below.
                    const summaryTotal = deferralSummary?.total_deferred ?? 0;
                    const providerShare =
                      topProvider && summaryTotal > 0
                        ? topProvider.count / summaryTotal
                        : 0;
                    const concentrated = providerShare >= 0.5;
                    return (
                      <div className="bg-amber-50 rounded-lg p-4 md:col-span-5 border border-amber-200">
                        <div className="text-xs text-amber-800 uppercase tracking-wide font-semibold">
                          {displayDeferred.toLocaleString()} deferred /
                          throttled by receiving mail servers
                        </div>
                        <div className="mt-1 text-sm text-amber-900">
                          These were accepted by SendGrid, but the receiving
                          provider hasn't given a final answer yet — it's
                          temporarily{" "}
                          <strong>rate-limiting (throttling)</strong> our mail.
                          SendGrid auto-retries for ~72h; most deliver late and
                          the rest bounce. This is normal in-flight mail,{" "}
                          <strong>not</strong> a stuck platform.
                        </div>
                        {deferralSummary &&
                          deferralSummary.top_reasons.length > 0 && (
                            <div className="mt-3 grid gap-3 md:grid-cols-2">
                              <div>
                                <div className="text-xs text-amber-700 uppercase tracking-wide">
                                  Top reasons
                                </div>
                                <ul className="mt-1 space-y-0.5 text-sm text-amber-900">
                                  {deferralSummary.top_reasons.map((r, i) => (
                                    <li
                                      key={i}
                                      className="flex justify-between gap-2"
                                    >
                                      <span
                                        className="truncate"
                                        title={r.reason}
                                      >
                                        {r.reason}
                                      </span>
                                      <span className="font-medium tabular-nums">
                                        {r.count.toLocaleString()}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <div className="text-xs text-amber-700 uppercase tracking-wide">
                                  By receiving domain
                                </div>
                                <ul className="mt-1 space-y-0.5 text-sm text-amber-900">
                                  {deferralSummary.top_providers.map((p, i) => (
                                    <li
                                      key={i}
                                      className="flex justify-between gap-2"
                                    >
                                      <span
                                        className="truncate"
                                        title={p.provider}
                                      >
                                        {p.provider}
                                      </span>
                                      <span className="font-medium tabular-nums">
                                        {p.count.toLocaleString()}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}
                        <div className="mt-3 text-sm text-amber-900 border-t border-amber-200 pt-2">
                          <strong>What to do:</strong>{" "}
                          {concentrated && topProvider ? (
                            <>
                              This pile-up is concentrated on{" "}
                              <strong>{topProvider.provider}</strong> (
                              {Math.round(providerShare * 100)}% of deferrals) —
                              that provider is throttling our sending IP harder
                              than usual. This is a{" "}
                              <strong>deliverability / infrastructure</strong>{" "}
                              matter: pace the sending to that provider, spread
                              volume across more sending IPs, and/or contact its
                              postmaster. It is <strong>not</strong> fixed by
                              editing the flight or the list.
                            </>
                          ) : (
                            <>
                              Check back over the next few hours — most of these
                              resolve on their own. If a large share is still
                              deferred after a day, or is concentrated on one
                              provider, treat it as a deliverability / IP matter
                              (pace &amp; spread volume; contact the provider's
                              postmaster) rather than a list problem.
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                {/* G4 bounce-source panel: WHY the send bounced — hard/soft/spam breakdown, top
                    provider reasons + receiving-domain concentration, and what to do. Only when
                    there's a bounce summary (fetched when emails_bounced > 0). */}
                {displayBounced > 0 &&
                  bounceSummary &&
                  bounceSummary.total_bounced > 0 &&
                  (() => {
                    const hard = bounceSummary.by_type["hard"] ?? 0;
                    const soft = bounceSummary.by_type["soft"] ?? 0;
                    const spam = bounceSummary.by_type["spam"] ?? 0;
                    const total = bounceSummary.total_bounced;
                    const hardHeavy = hard / total >= 0.5;
                    return (
                      <div className="bg-red-50 rounded-lg p-4 md:col-span-5 border border-red-200">
                        <div className="text-xs text-red-800 uppercase tracking-wide font-semibold">
                          {total.toLocaleString()} bounced — why
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-sm">
                          {hard > 0 && (
                            <span className="inline-flex items-center gap-1 rounded bg-red-100 text-red-800 px-2 py-0.5">
                              <strong className="tabular-nums">
                                {hard.toLocaleString()}
                              </strong>{" "}
                              hard (permanent — address doesn't exist)
                            </span>
                          )}
                          {soft > 0 && (
                            <span className="inline-flex items-center gap-1 rounded bg-amber-100 text-amber-800 px-2 py-0.5">
                              <strong className="tabular-nums">
                                {soft.toLocaleString()}
                              </strong>{" "}
                              soft (transient)
                            </span>
                          )}
                          {spam > 0 && (
                            <span className="inline-flex items-center gap-1 rounded bg-purple-100 text-purple-800 px-2 py-0.5">
                              <strong className="tabular-nums">
                                {spam.toLocaleString()}
                              </strong>{" "}
                              spam complaint{spam === 1 ? "" : "s"}
                            </span>
                          )}
                        </div>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                          <div>
                            <div className="text-xs text-red-700 uppercase tracking-wide">
                              Top reasons
                            </div>
                            <ul className="mt-1 space-y-0.5 text-sm text-red-900">
                              {bounceSummary.top_reasons.map((r, i) => (
                                <li
                                  key={i}
                                  className="flex justify-between gap-2"
                                >
                                  <span className="truncate" title={r.reason}>
                                    {r.reason}
                                  </span>
                                  <span className="font-medium tabular-nums">
                                    {r.count.toLocaleString()}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <div className="text-xs text-red-700 uppercase tracking-wide">
                              By receiving domain
                            </div>
                            <ul className="mt-1 space-y-0.5 text-sm text-red-900">
                              {bounceSummary.top_providers.map((p, i) => (
                                <li
                                  key={i}
                                  className="flex justify-between gap-2"
                                >
                                  <span className="truncate" title={p.provider}>
                                    {p.provider}
                                  </span>
                                  <span className="font-medium tabular-nums">
                                    {p.count.toLocaleString()}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="mt-3 text-sm text-red-900 border-t border-red-200 pt-2">
                          <strong>What to do:</strong>{" "}
                          {hardHeavy ? (
                            <>
                              Most of these are <strong>hard</strong> bounces —
                              permanent, because the address doesn't exist.
                              SendGrid auto-suppresses them, but a high
                              hard-bounce rate means the{" "}
                              <strong>list source is stale or bad</strong>:
                              check where the list came from and run the
                              pre-send list-quality check (it flags these before
                              they cost you reputation).
                            </>
                          ) : (
                            <>
                              Mostly <strong>soft</strong> (transient) bounces —
                              mailbox full or temporarily unavailable; these
                              often clear on their own.
                            </>
                          )}
                          {spam > 0 && (
                            <>
                              {" "}
                              The{" "}
                              <strong>
                                {spam.toLocaleString()} spam complaint
                                {spam === 1 ? "" : "s"}
                              </strong>{" "}
                              are a sender-reputation risk — review the content
                              and send frequency for this audience.
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}
              </div>
              <FlightOpenerExportPanel flightId={flight.id} />
              <FlightNonOpenerExportPanel flightId={flight.id} />
              <FlightUnsubscribeExportPanel flightId={flight.id} />
              <AudienceExclusionsPanel flight={flight} />
              {/* Automatic NeverBounce check (2026-10-08): read-only. Every address is checked
                  before it is emailed; nobody triggers it from here. */}
              <EmailCheckStatusLine
                flightId={flight.id}
                flightStatus={flight.status}
              />
              {flight.eib_launch_id &&
                (flight.eib_flight_role === "invite" ||
                  flight.eib_flight_role === "invite_reminder") && (
                  <EibExcludeRiskyToggle
                    flightId={flight.id}
                    flightStatus={flight.status}
                    excludeRisky={
                      (
                        flight.metadata as
                          | { exclude_risky?: unknown }
                          | undefined
                      )?.exclude_risky === true
                    }
                    onChanged={loadData}
                  />
                )}
              {/* Email template info */}
              {flight.email_template_id && (
                <div className="px-6 pb-6 pt-2 border-t border-gray-100 space-y-2">
                  <div className="flex items-center gap-4 text-sm">
                    {(flight.email_template_data as any)?.template_name && (
                      <span className="text-gray-600">
                        Template:{" "}
                        <span className="font-medium text-gray-900">
                          {(flight.email_template_data as any).template_name}
                        </span>
                      </span>
                    )}
                    {flight.email_subject && (
                      <span className="text-gray-600">
                        Subject:{" "}
                        <span className="font-medium text-gray-900">
                          {flight.email_subject}
                        </span>
                      </span>
                    )}
                  </div>
                  {(flight.email_template_data as any)?.field_mappings?.length >
                    0 && (
                    <div className="flex flex-wrap gap-1">
                      {(flight.email_template_data as any).field_mappings.map(
                        (mapping: any, i: number) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 text-xs font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded"
                          >
                            {`{{${mapping.templateField}}}`}
                            <span className="text-blue-400">→</span>
                            {mapping.sourceType === "custom"
                              ? mapping.customValue || "custom"
                              : `${mapping.sourceType}.${mapping.sourceField}`}
                          </span>
                        ),
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* SMS Performance — only if SMS channel configured */}
          {hasSMS && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">
                  SMS Performance
                </h2>
              </div>
              <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Sent
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {flight.sms_sent}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Delivered
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {flight.sms_delivered}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      ({pct(flight.sms_delivered, flight.sms_sent)})
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Failed
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {flight.sms_failed}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Voucher Performance — only if vouchers configured */}
          {hasVouchers && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">
                  Voucher Performance
                </h2>
                {hasAudienceAccounting && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    <span className="font-medium text-gray-700">
                      {rawAudienceCount!.toLocaleString()}
                    </span>{" "}
                    in source list ·{" "}
                    <span className="font-medium text-gray-700">
                      {eligibleCount!.toLocaleString()}
                    </span>{" "}
                    eligible for voucher generation ·{" "}
                    <span className="font-medium text-gray-700">
                      {(flight.vouchers_generated ?? 0).toLocaleString()}
                    </span>{" "}
                    generated
                    {typeof excludedBeforeSend === "number" &&
                      excludedBeforeSend > 0 && (
                        <>
                          {" · "}
                          <span className="font-medium text-amber-700">
                            {excludedBeforeSend.toLocaleString()}
                          </span>{" "}
                          excluded before generation (opt-out / suppressed /
                          missing email / audience filter)
                        </>
                      )}
                  </p>
                )}
              </div>
              {voucherHealth && (
                <div className="mx-6 mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <p className="font-semibold">
                    ⚠ Voucher generation needs attention
                  </p>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    {voucherHealth.incomplete && (
                      <li>
                        Generation was incomplete
                        {voucherHealth.missing > 0
                          ? ` — ${voucherHealth.missing} recipient(s) have no voucher`
                          : ""}
                        . Those recipients are skipped at send time (not sent a
                        blank voucher). Re-run voucher generation to fill the
                        gap.
                      </li>
                    )}
                    {voucherHealth.stale && (
                      <li>
                        These vouchers were generated for a different segment
                        than the flight's current one. Re-generate vouchers so
                        they match the current segment before sending.
                      </li>
                    )}
                  </ul>
                  <button
                    onClick={handleRegenerateVouchers}
                    disabled={regeneratingVouchers}
                    className="mt-2 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
                  >
                    {regeneratingVouchers
                      ? "Regenerating…"
                      : "Regenerate vouchers"}
                  </button>
                </div>
              )}
              <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Generated
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {flight.vouchers_generated}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Redeemed
                  </div>
                  <div className="text-2xl font-bold text-green-600 mt-1">
                    {flight.vouchers_redeemed}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      (
                      {pct(flight.vouchers_redeemed, flight.vouchers_generated)}
                      )
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Redemption Rate
                  </div>
                  <div className="text-2xl font-bold text-primary-600 mt-1">
                    {pct(flight.vouchers_redeemed, flight.vouchers_generated)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No channel configured */}
          {!hasEmail && !hasSMS && !hasVouchers && (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              <p>
                No channel configured for this flight. Edit the flight to add
                email or SMS.
              </p>
            </div>
          )}

          {/* Schedule Info */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Schedule</h2>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  Created
                </div>
                <div className="text-sm text-gray-900 mt-1">
                  {formatDate(flight.created_at)}
                </div>
              </div>
              {flight.scheduled_send_at && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Scheduled Send
                  </div>
                  <div className="text-sm text-gray-900 mt-1">
                    {formatScheduledDateTime(
                      flight.scheduled_send_at,
                      (flight.metadata as any)?.send_timezone,
                    )}
                  </div>
                </div>
              )}
              {flight.sent_at && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Sent At
                  </div>
                  <div className="text-sm text-gray-900 mt-1">
                    {formatDate(flight.sent_at)}
                  </div>
                </div>
              )}
              {flight.status === "scheduled" && (
                <p className="text-xs text-gray-400 italic mt-2">
                  Status will update automatically when delivery is confirmed
                  via SendGrid/Sinch webhooks.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* G3 pre-send validation Health dialog (block = 400, warn = 409) */}
      {g3Gate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => !g3Busy && setG3Gate(null)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h3
                className={`text-lg font-bold mb-2 ${g3Gate.kind === "block" ? "text-red-700" : "text-amber-700"}`}
              >
                {g3Gate.kind === "block"
                  ? "⚠ Pre-send validation blocked this send"
                  : "⚠ This list has a deliverability warning"}
              </h3>
              <p className="text-sm text-gray-700 mb-3">
                {g3Gate.reason === "synthetic"
                  ? `${(g3Gate.synthetic ?? 0).toLocaleString()} of ${(g3Gate.total ?? 0).toLocaleString()} recipients (${((g3Gate.synthetic_ratio ?? 0) * 100).toFixed(1)}%) have a name followed by digits (for example jane.smith123@…). This is for information only — the pattern alone does not mean an address is bad.`
                  : g3Gate.reason === "invalid"
                    ? `${(g3Gate.invalid ?? 0).toLocaleString()} of ${(g3Gate.total ?? 0).toLocaleString()} recipients (${((g3Gate.invalid_ratio ?? 0) * 100).toFixed(1)}%) have an invalid format or a disposable email domain. Sending to addresses that cannot receive mail hurts deliverability for your future email.`
                    : `${(g3Gate.role ?? 0).toLocaleString()} of ${(g3Gate.total ?? 0).toLocaleString()} recipients (${((g3Gate.role_ratio ?? 0) * 100).toFixed(1)}%) are role addresses (info@, sales@, …). This is for information only — role addresses are not suppressed.`}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm mb-3 text-blue-900">
                <strong>Recommended:</strong> check with whoever provided this
                list.
                {g3FlaggedCount > 0 &&
                  " You can also suppress the invalid addresses before sending; other addresses are not affected."}
              </div>
              <div className="bg-gray-50 border rounded p-3 text-sm mb-3 space-y-1">
                <div>
                  Total recipients:{" "}
                  <span className="font-medium">
                    {(g3Gate.total ?? 0).toLocaleString()}
                  </span>
                </div>
                {(g3Gate.invalid ?? 0) > 0 && (
                  <div>
                    Invalid / disposable:{" "}
                    <span className="font-medium text-red-600">
                      {(g3Gate.invalid ?? 0).toLocaleString()}
                    </span>
                  </div>
                )}
                {(g3Gate.synthetic ?? 0) > 0 && (
                  <div>
                    Name followed by digits (informational, not suppressed):{" "}
                    <span className="font-medium text-gray-700">
                      {(g3Gate.synthetic ?? 0).toLocaleString()}
                    </span>
                  </div>
                )}
                {(g3Gate.role ?? 0) > 0 && (
                  <div>
                    Role addresses (informational, not suppressed):{" "}
                    <span className="font-medium text-gray-700">
                      {(g3Gate.role ?? 0).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
              {g3Gate.kind === "block" && g3Gate.context === "send" && (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason (required to send anyway)
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded p-2 text-sm mb-4"
                    rows={2}
                    value={g3OverrideReason}
                    onChange={(e) => setG3OverrideReason(e.target.value)}
                    placeholder="e.g. Reviewed the invalid addresses; valid for this send."
                  />
                </>
              )}
              {g3Gate.kind === "block" && g3Gate.context === "schedule" && (
                <p className="text-sm text-gray-600 mb-4">
                  A scheduled send can't be overridden — suppress the invalid
                  addresses or fix the list at its source, then schedule.
                </p>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => {
                    setG3Gate(null);
                    setG3OverrideReason("");
                  }}
                  disabled={g3Busy}
                >
                  Stop
                </button>
                {g3FlaggedCount > 0 && (
                  <button
                    className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    onClick={handleG3Suppress}
                    disabled={g3Busy}
                    title="Adds only the invalid addresses (bad format or disposable domain) to the global suppression list, then sends to everyone else (admin only). Name-and-digits, role and other addresses are not suppressed."
                  >
                    {g3Busy
                      ? "Working…"
                      : `Suppress ${g3FlaggedCount.toLocaleString()} invalid & ${g3Gate.context === "schedule" ? "schedule" : "send"}`}
                  </button>
                )}
                {g3Gate.kind === "block" && g3Gate.context === "send" && (
                  <button
                    className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    onClick={handleG3Override}
                    disabled={g3Busy || !g3OverrideReason.trim()}
                  >
                    {g3Busy ? "Sending…" : "Confirm & send anyway"}
                  </button>
                )}
                {g3Gate.kind === "warn" && (
                  <button
                    className="px-4 py-2 text-sm rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                    onClick={handleG3WarnContinue}
                    disabled={g3Busy}
                  >
                    {g3Busy
                      ? "Working…"
                      : g3Gate.context === "schedule"
                        ? "Continue & schedule"
                        : "Continue & send"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Delete Flight
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete "{flight.name}"? This action
                cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {submitting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      <TemplatePreviewModal
        isOpen={showPreview && !!templateDetails?.html_content}
        onClose={() => setShowPreview(false)}
        templateHtml={templateDetails?.html_content || ""}
        templateName={templateDetails?.name}
        fieldMappings={
          ((flight?.email_template_data as any)
            ?.field_mappings as FieldMapping[]) || []
        }
        hasEmailCategory={
          flight ? !!(flight as any).subscription_group_id : undefined
        }
        flightSubject={flight ? (flight.email_subject ?? null) : undefined}
        flightPreheader={
          flight
            ? ((flight.metadata?.preheader as string | undefined) ?? null)
            : undefined
        }
      />

      {/* Phase 1B: Pre-send confirmation gate at the schedule action.
          Modal owns its own template fetch and audience-count fetch — both
          are hard blockers if they fail. */}
      {flight && (
        <PreSendConfirmationModal
          isOpen={showPreSendModal}
          flight={flight as any}
          listName={(flight as any).list?.name}
          onClose={() => setShowPreSendModal(false)}
          onConfirm={async () => {
            setShowPreSendModal(false);
            await handleScheduleConfirm();
          }}
        />
      )}

      {/* Send Now — same pre-send validation as Schedule, but the
          confirm action fires send-campaign-flight immediately. The send
          loop takes ~30-60s for a typical 2,990-recipient send; the page
          shows a "Sending…" overlay while the fetch is in flight. */}
      {flight && (
        <PreSendConfirmationModal
          isOpen={showSendNowModal}
          flight={flight as any}
          listName={(flight as any).list?.name}
          mode="send_now"
          onClose={() => setShowSendNowModal(false)}
          onConfirm={async () => {
            setShowSendNowModal(false);
            await handleSendNow();
          }}
        />
      )}

      {/* Sending-in-progress blocker. Stays up while handleSendNow's
          fetch is awaiting; the SendGrid handoff is synchronous and
          can take a minute for large audiences. */}
      {sendingNow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-900 font-medium">Sending flight…</p>
            <p className="text-sm text-gray-500 mt-1">
              Don't close this tab. Large audiences can take up to a minute
              while emails are handed off to SendGrid.
            </p>
          </div>
        </div>
      )}

      {/* Edit Schedule modal — datetime-local input pre-filled with
          current scheduled time in operator's local timezone. */}
      {showEditScheduleModal && flight && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Edit schedule</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled send time (your local time)
                </label>
                <input
                  type="datetime-local"
                  value={editScheduleAt}
                  onChange={(e) => setEditScheduleAt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  disabled={submitting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  The cron worker polls every 2 minutes, so the actual send
                  fires within ~2 minutes of this time.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowEditScheduleModal(false);
                  setOpenPreSendAfterScheduleSave(false);
                }}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              {/* 2026-05-27 (Bug A): primary action is Save when a date
                  is present; Clear when the operator emptied the input.
                  Clear maps to the same unschedule path as the dedicated
                  Cancel Schedule confirm modal. */}
              <button
                type="button"
                onClick={handleSaveSchedule}
                disabled={submitting}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${
                  editScheduleAt
                    ? "bg-primary-600 hover:bg-primary-700"
                    : "bg-gray-600 hover:bg-gray-700"
                }`}
              >
                {submitting
                  ? "Saving…"
                  : editScheduleAt
                    ? "Save schedule"
                    : "Clear schedule"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Schedule confirmation — reverts to draft + clears
          scheduled_send_at. The worker filter then leaves it alone. */}
      {showCancelScheduleConfirm && flight && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {flight.eib_launch_id
                  ? "Cancel this invitation?"
                  : "Cancel scheduled send?"}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-3">
              {flight.eib_launch_id ? (
                <p className="text-sm text-gray-700">
                  This Event in a Box invitation will be{" "}
                  <strong>cancelled</strong> and will not send. It cannot be
                  re-scheduled — its audience and design were fixed when the
                  event was set up. The event itself is unchanged; run the event
                  setup again to send a new invitation.
                </p>
              ) : (
                <p className="text-sm text-gray-700">
                  This flight will return to <strong>draft</strong> status and
                  will not send at its scheduled time. You can edit and
                  re-schedule (or send now) afterward.
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCancelScheduleConfirm(false)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Keep schedule
              </button>
              <button
                type="button"
                onClick={handleCancelSchedule}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {submitting ? "Cancelling…" : "Cancel schedule"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 1B: Send a test render to operator-supplied recipients */}
      {flight && (
        <SendTestEmailModal
          isOpen={showTestSendModal}
          flightId={flight.id}
          flightName={flight.name}
          onClose={() => setShowTestSendModal(false)}
        />
      )}
    </div>
  );
};

export default FlightDetail;
